import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLearningStage } from '../context/LearningStageContext';
import { Button } from '../components/ui/button';
import { 
  Home, Sparkles, Compass, Map, BookOpen, Settings, LogOut, Menu, X,
  User, Brain, Star, Rocket, Target, Award, Heart, ChevronDown, ChevronRight,
  Sun, Moon
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Middle School Navigation Items
const MS_NAV_ITEMS = [
  {
    id: 'home',
    items: [
      { path: '/ms/home', icon: Home, label: 'My Space', color: 'blue' }
    ]
  },
  {
    id: 'discover',
    label: 'Discover Me',
    icon: Sparkles,
    items: [
      { path: '/ms/about-me', icon: User, label: 'Who Am I?', color: 'purple' },
      { path: '/ms/quiz', icon: Brain, label: 'Personality Quiz', color: 'purple' },
      { path: '/ms/strengths', icon: Star, label: 'My Superpowers', color: 'yellow' }
    ]
  },
  {
    id: 'explore',
    label: 'Explore Futures',
    icon: Compass,
    items: [
      { path: '/ms/careers', icon: Rocket, label: 'Career Cards', color: 'blue' },
      { path: '/ms/future-jobs', icon: Rocket, label: 'Future Jobs', color: 'cyan' },
      { path: '/ms/dream-life', icon: Heart, label: 'Dream Life Quiz', color: 'pink' }
    ]
  },
  {
    id: 'adventures',
    label: 'My Adventures',
    icon: Map,
    items: [
      { path: '/ms/dreams', icon: Target, label: 'My Dreams', color: 'amber' },
      { path: '/ms/missions', icon: Target, label: "This Week's Quest", color: 'orange' },
      { path: '/ms/programs', icon: Award, label: 'Cool Programs', color: 'green' }
    ]
  },
  {
    id: 'learn',
    label: 'Learn',
    icon: BookOpen,
    items: [
      { path: '/ms/learn', icon: BookOpen, label: 'Learning Portal', color: 'cyan' },
      { path: '/ms/badges', icon: Award, label: 'My Badges', color: 'yellow' }
    ]
  },
  {
    id: 'account',
    label: 'My Stuff',
    icon: Settings,
    items: [
      { path: '/ms/profile', icon: User, label: 'My Profile', color: 'slate' }
    ]
  }
];

// Simplified Navigation Item for Middle School (sub-items - smaller text)
const MSNavItem = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
        isActive 
          ? `bg-${item.color}-500/20 text-${item.color}-400 border border-${item.color}-500/30` 
          : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm">{item.label}</span>
    </Link>
  );
};

// Navigation Group (section headers - LARGER text)
const MSNavGroup = ({ group, isExpanded, onToggle, currentPath }) => {
  const Icon = group.icon;
  const hasActiveItem = group.items?.some(item => item.path === currentPath);
  
  // If no label, render items directly (home section)
  if (!group.label) {
    return (
      <div className="space-y-1">
        {group.items.map(item => (
          <MSNavItem 
            key={item.path} 
            item={item} 
            isActive={currentPath === item.path}
          />
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
          hasActiveItem ? 'text-white bg-slate-700/40' : 'text-slate-200 hover:text-white hover:bg-slate-700/40'
        }`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" />
          <span className="text-base font-semibold">{group.label}</span>
        </div>
        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="ml-4 space-y-1"
          >
            {group.items.map(item => (
              <MSNavItem 
                key={item.path} 
                item={item} 
                isActive={currentPath === item.path}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function MiddleSchoolLayout() {
  const { user, logout } = useAuth();
  const { learningStage } = useLearningStage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(['discover', 'explore', 'adventures', 'learn']);
  
  const firstName = user?.name?.split(' ')[0] || 'Explorer';
  
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(g => g !== groupId)
        : [...prev, groupId]
    );
  };
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-800/95 backdrop-blur-lg border-b border-slate-600">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <Compass className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-semibold">FuturePath</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-slate-300 hover:text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '-100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '-100%' }}
            className="lg:hidden fixed inset-0 z-40 bg-slate-800/98 pt-16"
          >
            <nav className="p-4 space-y-2 overflow-y-auto max-h-[calc(100vh-4rem)]">
              {MS_NAV_ITEMS.map(group => (
                <MSNavGroup
                  key={group.id}
                  group={group}
                  isExpanded={expandedGroups.includes(group.id)}
                  onToggle={() => toggleGroup(group.id)}
                  currentPath={location.pathname}
                />
              ))}
              
              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-all mt-4"
              >
                <LogOut className="w-5 h-5" />
                <span>Sign Out</span>
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col fixed left-0 top-0 bottom-0 z-40 bg-slate-800/95 backdrop-blur-lg border-r border-slate-600 transition-all ${
        sidebarOpen ? 'w-64' : 'w-20'
      }`}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <Compass className="w-6 h-6 text-white" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="text-white font-bold">FuturePath</h1>
                <p className="text-xs text-slate-300">Explorer Mode</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {MS_NAV_ITEMS.map(group => (
            <MSNavGroup
              key={group.id}
              group={group}
              isExpanded={sidebarOpen && expandedGroups.includes(group.id)}
              onToggle={() => toggleGroup(group.id)}
              currentPath={location.pathname}
            />
          ))}
        </nav>
        
        {/* User Section */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
              {firstName.charAt(0)}
            </div>
            {sidebarOpen && (
              <div>
                <p className="text-white font-medium text-sm">{firstName}</p>
                <p className="text-slate-300 text-xs">Explorer</p>
              </div>
            )}
          </div>
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all text-sm"
          >
            <LogOut className="w-4 h-4" />
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className={`pt-16 lg:pt-0 transition-all ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <div className="min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
